import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Notifications as Bell,
  Save,
  People as Users,
  Campaign as CampaignIcon,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import theme from '../../../theme/theme';
import { api } from '../../../services/authService';
import useLocalStorage from '../../../hooks/useLocalStorage';

const NotificationSettings = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedAccount, setSelectedAccount] = useLocalStorage('selectedAccount', '');
  const [selectedCampaign, setSelectedCampaign] = useLocalStorage('selectedCampaign', '');
  const [isSelectionPaneExpanded, setIsSelectionPaneExpanded] = useState(true);

  // State for accounts and campaigns
  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [preferencesLoading, setPreferencesLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Notification preferences state
  const [notificationPrefs, setNotificationPrefs] = useState({
    global: {
      general: {
        messageReplies: false,
        systemUpdates: false,
        notificationMethod: 'both',
        emailDigest: 'daily',
        desktopNotifications: true,
        mobileNotifications: true,
        emailNotifications: true,
        inAppNotifications: true,
        smsNotifications: false
      },
    },
    accounts: {},
    campaigns: {}
  });

  // Delivery preferences
  const [deliveryPrefs, setDeliveryPrefs] = useState({
    accounts: {
      email: false,
      inApp: false,
      push: false,
    },
    campaigns: {
      email: false,
      inApp: false,
      push: false,
    }
  });

  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [hasPreferences, setHasPreferences] = useState(false);

  // Memoize the account change handler
  const handleAccountChange = useCallback((event) => {
    const newAccountId = event.target.value;
    setSelectedAccount(newAccountId);
    localStorage.setItem('selectedAccount', newAccountId);
  }, []);

  // Memoize the campaign change handler
  const handleCampaignChange = useCallback((event) => {
    const newCampaignId = event.target.value;
    setSelectedCampaign(newCampaignId);
    localStorage.setItem('selectedCampaign', newCampaignId);
  }, []);

  // Fetch accounts with optimized loading
  useEffect(() => {
    const fetchAccounts = async () => {
      setAccountsLoading(true);
      try {
        const response = await api.get('/crm/accounts/');
        const fetchedAccounts = response.data;
        setAccounts(fetchedAccounts);

        // Get stored account from localStorage
        const storedAccount = localStorage.getItem('selectedAccount');
        
        // Validate and set selected account
        if (storedAccount && fetchedAccounts.some(acc => String(acc.id) === storedAccount)) {
          setSelectedAccount(storedAccount);
        } else if (fetchedAccounts.length > 0) {
          // If no valid stored account, select the first one
          const firstAccountId = String(fetchedAccounts[0].id);
          setSelectedAccount(firstAccountId);
          localStorage.setItem('selectedAccount', firstAccountId);
        }
      } catch (error) {
        console.error('Error fetching accounts:', error);
        showSnackbar('Failed to fetch accounts', 'error');
      } finally {
        setAccountsLoading(false);
        setInitialLoadComplete(true);
      }
    };

    fetchAccounts();
  }, []); // Remove setSelectedAccount from dependencies

  // Fetch global preferences
  useEffect(() => {
    const fetchGlobalPreferences = async () => {
      setPreferencesLoading(true);
      try {
        const response = await api.get('/crm/notification-preferences/global/');
        const prefs = response.data;
        setHasPreferences(true);
        setNotificationPrefs(prev => ({
          ...prev,
          global: {
            general: {
              messageReplies: prefs.message_replies,
              systemUpdates: prefs.system_updates,
              notificationMethod: prefs.notification_method,
              emailDigest: prefs.email_digest,
              desktopNotifications: prefs.desktop_notifications,
              mobileNotifications: prefs.mobile_notifications,
              emailNotifications: prefs.email_notifications,
              inAppNotifications: prefs.in_app_notifications,
              smsNotifications: prefs.sms_notifications
            },
          },
        }));
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setShowSetupDialog(true);
          setHasPreferences(false);
        } else {
          console.error('Error fetching global preferences:', error);
          showSnackbar('Failed to fetch global preferences', 'error');
        }
      } finally {
        setPreferencesLoading(false);
      }
    };
    fetchGlobalPreferences();
  }, []);

  // Fetch account preferences when account is selected
  useEffect(() => {
    const fetchAccountPreferences = async () => {
      if (selectedAccount) {
        setPreferencesLoading(true);
        try {
          const response = await api.get(`/crm/notification-preferences/accounts/?account_id=${selectedAccount}`);
          const prefs = response.data[0] || {}; // Get first preference or empty object
          setNotificationPrefs(prev => ({
            ...prev,
            accounts: {
              ...prev.accounts,
              [selectedAccount]: {
                newUserAdded: prefs.new_user_added || false,
                userRoleChanged: prefs.user_role_changed || false,
              },
            },
          }));
          setDeliveryPrefs(prev => ({
            ...prev,
            accounts: {
              email: prefs.email_delivery || false,
              inApp: prefs.in_app_delivery || false,
              push: prefs.sms_delivery || false,
            },
          }));
        } catch (error) {
          console.error('Error fetching account preferences:', error);
          showSnackbar('Failed to fetch account preferences', 'error');
        } finally {
          setPreferencesLoading(false);
        }
      }
    };
    fetchAccountPreferences();
  }, [selectedAccount]);

  // Fetch campaign preferences when campaign is selected
  useEffect(() => {
    const fetchCampaignPreferences = async () => {
      if (selectedAccount && selectedCampaign) {
        setPreferencesLoading(true);
        try {
          const response = await api.get(
            `/crm/notification-preferences/campaigns/?campaign_id=${selectedCampaign}`
          );
          const prefs = response.data[0] || {}; // Get first preference or empty object
          setNotificationPrefs(prev => ({
            ...prev,
            campaigns: {
              ...prev.campaigns,
              [selectedCampaign]: {
                leadPosted: prefs.lead_posted || false,
                appointmentCreated: prefs.appointment_created || false,
                liveTransfer: prefs.live_transfer || false,
              },
            },
          }));
          setDeliveryPrefs(prev => ({
            ...prev,
            campaigns: {
              email: prefs.email_delivery || false,
              inApp: prefs.in_app_delivery || false,
              push: prefs.sms_delivery || false,
            },
          }));
        } catch (error) {
          console.error('Error fetching campaign preferences:', error);
          showSnackbar('Failed to fetch campaign preferences', 'error');
        } finally {
          setPreferencesLoading(false);
        }
      }
    };
    fetchCampaignPreferences();
  }, [selectedAccount, selectedCampaign]);

  // Validate selectedAccount
  useEffect(() => {
    if (!accountsLoading && selectedAccount) {
      const accountExists = accounts.some((a) => String(a.id) === String(selectedAccount));
      if (!accountExists) {
        setSelectedAccount('');
      }
    }
  }, [accountsLoading, accounts, selectedAccount]);

  // Fetch campaigns when account changes
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (selectedAccount) {
        setCampaignsLoading(true);
        try {
          const response = await api.get(`/crm/campaigns/?account_id=${selectedAccount}`);
          const fetchedCampaigns = response.data;
          setCampaigns(fetchedCampaigns);

          // Get stored campaign from localStorage
          const storedCampaign = localStorage.getItem('selectedCampaign');
          
          // Validate and set selected campaign
          if (storedCampaign && fetchedCampaigns.some(camp => String(camp.id) === storedCampaign)) {
            setSelectedCampaign(storedCampaign);
          } else if (fetchedCampaigns.length > 0) {
            // If no valid stored campaign, select the first one
            const firstCampaignId = String(fetchedCampaigns[0].id);
            setSelectedCampaign(firstCampaignId);
            localStorage.setItem('selectedCampaign', firstCampaignId);
          } else {
            setSelectedCampaign('');
          }
        } catch (error) {
          console.error('Error fetching campaigns:', error);
          showSnackbar('Failed to fetch campaigns', 'error');
        } finally {
          setCampaignsLoading(false);
        }
      } else {
        setCampaigns([]);
        setSelectedCampaign('');
      }
    };

    fetchCampaigns();
  }, [selectedAccount]); // Only depend on selectedAccount

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleSavePreferences = async () => {
    setLoading(true);
    try {
      // Validate that at least one notification method is enabled for each type
      const hasAccountDelivery = selectedAccount && (
        deliveryPrefs.accounts.email || 
        deliveryPrefs.accounts.inApp || 
        deliveryPrefs.accounts.push
      );

      const hasCampaignDelivery = selectedCampaign && (
        deliveryPrefs.campaigns.email || 
        deliveryPrefs.campaigns.inApp || 
        deliveryPrefs.campaigns.push
      );

      const hasGlobalDelivery = 
        notificationPrefs.global.general.emailNotifications || 
        notificationPrefs.global.general.inAppNotifications || 
        notificationPrefs.global.general.smsNotifications;

      if (!hasGlobalDelivery && !hasAccountDelivery && !hasCampaignDelivery) {
        showSnackbar('At least one notification method must be enabled', 'error');
        return;
      }

      // Get the current global notification preferences
      const globalEmail = Boolean(notificationPrefs.global.general.emailNotifications);
      const globalInApp = Boolean(notificationPrefs.global.general.inAppNotifications);
      const globalSMS = Boolean(notificationPrefs.global.general.smsNotifications);

      const payload = {
        global_preferences: {
          message_replies: notificationPrefs.global.general.messageReplies,
          system_updates: notificationPrefs.global.general.systemUpdates,
          email_digest: notificationPrefs.global.general.emailDigest,
          desktop_notifications: notificationPrefs.global.general.desktopNotifications,
          mobile_notifications: notificationPrefs.global.general.mobileNotifications,
          email_notifications: globalEmail,
          in_app_notifications: globalInApp,
          sms_notifications: globalSMS
        },
        account_preferences: {},
        campaign_preferences: {}
      };

      // Add account-specific settings if an account is selected
      if (selectedAccount) {
        payload.account_preferences[selectedAccount] = {
          new_user_added: notificationPrefs.accounts[selectedAccount]?.newUserAdded || false,
          user_role_changed: notificationPrefs.accounts[selectedAccount]?.userRoleChanged || false,
          email_delivery: deliveryPrefs.accounts.email,
          in_app_delivery: deliveryPrefs.accounts.inApp,
          sms_delivery: deliveryPrefs.accounts.push
        };
      }

      // Add campaign-specific settings if a campaign is selected
      if (selectedCampaign) {
        payload.campaign_preferences[selectedCampaign] = {
          lead_posted: notificationPrefs.campaigns[selectedCampaign]?.leadPosted || false,
          appointment_created: notificationPrefs.campaigns[selectedCampaign]?.appointmentCreated || false,
          live_transfer: notificationPrefs.campaigns[selectedCampaign]?.liveTransfer || false,
          email_delivery: deliveryPrefs.campaigns.email,
          in_app_delivery: deliveryPrefs.campaigns.inApp,
          sms_delivery: deliveryPrefs.campaigns.push
        };
      }

      await api.post('/crm/notification-preferences/bulk/', payload);
      showSnackbar('Notification preferences saved successfully');
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      showSnackbar('Failed to save notification preferences', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleSetupDialogClose = () => {
    setShowSetupDialog(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', color: theme.palette.text.primary, alignItems: 'center', mb: 3 }}>
        <Bell sx={{ mr: 1 }} />
        <Typography variant="h4">Notification Settings</Typography>
      </Box>

      {/* Account and Campaign Selection */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Account and Campaign Selection"
          subheader="Select an account and campaign to configure specific notification settings"
          action={
            <IconButton onClick={() => setIsSelectionPaneExpanded(!isSelectionPaneExpanded)}>
              {isSelectionPaneExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          }
        />
        {isSelectionPaneExpanded && (
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Select Account</InputLabel>
                  <Select
                    value={selectedAccount}
                    onChange={handleAccountChange}
                    label="Select Account"
                    disabled={accountsLoading}
                  >
                    {accountsLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading accounts...
                      </MenuItem>
                    ) : accounts.length === 0 ? (
                      <MenuItem disabled>No accounts available</MenuItem>
                    ) : (
                      accounts.map((account) => (
                        <MenuItem key={account.id} value={String(account.id)}>
                          {account.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Select Campaign</InputLabel>
                  <Select
                    value={selectedCampaign}
                    onChange={handleCampaignChange}
                    label="Select Campaign"
                    disabled={!selectedAccount || campaignsLoading}
                  >
                    {campaignsLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading campaigns...
                      </MenuItem>
                    ) : (
                      campaigns.map((campaign) => (
                        <MenuItem key={campaign.id} value={String(campaign.id)}>
                          {campaign.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        )}
      </Card>

      <Tabs value={selectedTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab 
          icon={<Users />} 
          label="Account Notifications" 
          disabled={!selectedAccount}
        />
        <Tab 
          icon={<CampaignIcon />} 
          label="Campaign Notifications" 
          disabled={!selectedCampaign}
        />
        <Tab icon={<Bell />} label="General Settings" />
      </Tabs>

      {/* Account Notifications Tab */}
      {selectedTab === 0 && selectedAccount && (
        <Card>
          <CardHeader
            title="Account Notifications"
            subheader="Control notifications related to account activities and changes"
          />
          <CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={notificationPrefs.accounts[selectedAccount]?.newUserAdded || false}
                  onChange={(e) => {
                    setNotificationPrefs({
                      ...notificationPrefs,
                      accounts: {
                        ...notificationPrefs.accounts,
                        [selectedAccount]: {
                          ...notificationPrefs.accounts[selectedAccount],
                          newUserAdded: e.target.checked,
                        },
                      },
                    });
                  }}
                />
              }
              label="New user added"
            />
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={notificationPrefs.accounts[selectedAccount]?.userRoleChanged || false}
                  onChange={(e) => {
                    setNotificationPrefs({
                      ...notificationPrefs,
                      accounts: {
                        ...notificationPrefs.accounts,
                        [selectedAccount]: {
                          ...notificationPrefs.accounts[selectedAccount],
                          userRoleChanged: e.target.checked,
                        },
                      },
                    });
                  }}
                />
              }
              label="User role changed"
            />
            <Divider sx={{ my: 2 }} />
            
            {/* Account Delivery Preferences */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Account Notification Delivery
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Choose how you want to receive account notifications
              </Typography>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(deliveryPrefs.accounts.email)}
                        onChange={(e) =>
                          setDeliveryPrefs({
                            ...deliveryPrefs,
                            accounts: { ...deliveryPrefs.accounts, email: e.target.checked },
                          })
                        }
                      />
                    }
                    label="Email notifications"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(deliveryPrefs.accounts.inApp)}
                        onChange={(e) =>
                          setDeliveryPrefs({
                            ...deliveryPrefs,
                            accounts: { ...deliveryPrefs.accounts, inApp: e.target.checked },
                          })
                        }
                      />
                    }
                    label="In-app notifications"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(deliveryPrefs.accounts.push)}
                        onChange={(e) =>
                          setDeliveryPrefs({
                            ...deliveryPrefs,
                            accounts: { ...deliveryPrefs.accounts, push: e.target.checked },
                          })
                        }
                      />
                    }
                    label="SMS notifications"
                  />
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Campaign Notifications Tab */}
      {selectedTab === 1 && selectedCampaign && (
        <Card>
          <CardHeader
            title="Campaign Notifications"
            subheader="Control notifications related to campaign activities and leads"
          />
          <CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={notificationPrefs.campaigns[selectedCampaign]?.leadPosted || false}
                  onChange={(e) => {
                    setNotificationPrefs({
                      ...notificationPrefs,
                      campaigns: {
                        ...notificationPrefs.campaigns,
                        [selectedCampaign]: {
                          ...notificationPrefs.campaigns[selectedCampaign],
                          leadPosted: e.target.checked,
                        },
                      },
                    });
                  }}
                />
              }
              label="Lead posted"
            />
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={notificationPrefs.campaigns[selectedCampaign]?.appointmentCreated || false}
                  onChange={(e) => {
                    setNotificationPrefs({
                      ...notificationPrefs,
                      campaigns: {
                        ...notificationPrefs.campaigns,
                        [selectedCampaign]: {
                          ...notificationPrefs.campaigns[selectedCampaign],
                          appointmentCreated: e.target.checked,
                        },
                      },
                    });
                  }}
                />
              }
              label="Appointment created"
            />
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={notificationPrefs.campaigns[selectedCampaign]?.liveTransfer || false}
                  onChange={(e) => {
                    setNotificationPrefs({
                      ...notificationPrefs,
                      campaigns: {
                        ...notificationPrefs.campaigns,
                        [selectedCampaign]: {
                          ...notificationPrefs.campaigns[selectedCampaign],
                          liveTransfer: e.target.checked,
                        },
                      },
                    });
                  }}
                />
              }
              label="Live Transfers"
            />
            <Divider sx={{ my: 2 }} />
            
            {/* Campaign Delivery Preferences */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Campaign Notification Delivery
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Choose how you want to receive campaign notifications
              </Typography>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(deliveryPrefs.campaigns.email)}
                        onChange={(e) =>
                          setDeliveryPrefs({
                            ...deliveryPrefs,
                            campaigns: { ...deliveryPrefs.campaigns, email: e.target.checked },
                          })
                        }
                      />
                    }
                    label="Email notifications"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(deliveryPrefs.campaigns.inApp)}
                        onChange={(e) =>
                          setDeliveryPrefs({
                            ...deliveryPrefs,
                            campaigns: { ...deliveryPrefs.campaigns, inApp: e.target.checked },
                          })
                        }
                      />
                    }
                    label="In-app notifications"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(deliveryPrefs.campaigns.push)}
                        onChange={(e) =>
                          setDeliveryPrefs({
                            ...deliveryPrefs,
                            campaigns: { ...deliveryPrefs.campaigns, push: e.target.checked },
                          })
                        }
                      />
                    }
                    label="SMS notifications"
                  />
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* General Settings Tab */}
      {selectedTab === 2 && (
        <Card>
          <CardHeader
            title="General Settings"
            subheader="Configure your overall notification preferences"
          />
          <CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={notificationPrefs.global.general.messageReplies}
                  onChange={(e) => {
                    setNotificationPrefs({
                      ...notificationPrefs,
                      global: {
                        ...notificationPrefs.global,
                        general: {
                          ...notificationPrefs.global.general,
                          messageReplies: e.target.checked,
                        },
                      },
                    });
                  }}
                />
              }
              label="Message replies"
            />
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={notificationPrefs.global.general.systemUpdates}
                  onChange={(e) => {
                    setNotificationPrefs({
                      ...notificationPrefs,
                      global: {
                        ...notificationPrefs.global,
                        general: {
                          ...notificationPrefs.global.general,
                          systemUpdates: e.target.checked,
                        },
                      },
                    });
                  }}
                />
              }
              label="System updates"
            />
            <Divider sx={{ my: 2 }} />
            
            {/* Global Delivery Preferences */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Global Notification Delivery
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Choose how you want to receive general notifications
              </Typography>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(notificationPrefs.global.general.emailNotifications)}
                        onChange={(e) => {
                          setNotificationPrefs({
                            ...notificationPrefs,
                            global: {
                              ...notificationPrefs.global,
                              general: {
                                ...notificationPrefs.global.general,
                                emailNotifications: e.target.checked,
                              },
                            },
                          });
                        }}
                      />
                    }
                    label="Email notifications"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(notificationPrefs.global.general.inAppNotifications)}
                        onChange={(e) => {
                          setNotificationPrefs({
                            ...notificationPrefs,
                            global: {
                              ...notificationPrefs.global,
                              general: {
                                ...notificationPrefs.global.general,
                                inAppNotifications: e.target.checked,
                              },
                            },
                          });
                        }}
                      />
                    }
                    label="In-app notifications"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(notificationPrefs.global.general.smsNotifications)}
                        onChange={(e) => {
                          setNotificationPrefs({
                            ...notificationPrefs,
                            global: {
                              ...notificationPrefs.global,
                              general: {
                                ...notificationPrefs.global.general,
                                smsNotifications: e.target.checked,
                              },
                            },
                          });
                        }}
                      />
                    }
                    label="SMS notifications"
                  />
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <Save />}
          onClick={handleSavePreferences}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Notification Settings'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Setup Dialog */}
      <Dialog
        open={showSetupDialog}
        onClose={handleSetupDialogClose}
        aria-labelledby="setup-dialog-title"
        aria-describedby="setup-dialog-description"
      >
        <DialogTitle id="setup-dialog-title">
          Welcome to Notification Settings
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="setup-dialog-description">
            It looks like you haven't set up your notification preferences yet. 
            This is where you can customize how you receive notifications for:
            <ul>
              <li>Account activities and changes</li>
              <li>Campaign updates and lead activities</li>
              <li>System messages and updates</li>
            </ul>
            You can choose to receive notifications via email, in-app messages, or SMS.
            Take a moment to configure your preferences to ensure you don't miss any important updates.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSetupDialogClose} color="primary">
            Let's Get Started
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationSettings; 